import React, { useEffect, useMemo, useState } from "react";
import { staffApi } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  Trash2, History, Clock, X, Ticket, CheckCircle2,
  Calendar as CalendarIcon, Pencil, CreditCard, User as UserIcon,
  FileText, RefreshCcw, ShieldAlert, PlusCircle, Ban, Users, Percent,
} from "lucide-react";
import { fmtDateTime } from "./dateFmt";

/* ---------------- Inline audit-trail (History) panel ----------------
 * Renders under the main Order card so managers see the full
 * timeline without opening a modal. Fetches the analytics-friendly
 * `/orders/{oid}/events` endpoint (kept fresh by every mutation) and
 * groups events by day. Icon + colour scheme keys off the event
 * `category` returned by the backend.
 * ------------------------------------------------------------------ */
const CATEGORY_META = {
  lifecycle:    { label: "Lifecycle",    color: "bg-slate-100 text-slate-800 border-slate-200", icon: FileText },
  booking:      { label: "Booking",      color: "bg-sky-100 text-sky-900 border-sky-200",       icon: CalendarIcon },
  payment:      { label: "Payment",      color: "bg-emerald-100 text-emerald-900 border-emerald-200", icon: CreditCard },
  modification: { label: "Modification", color: "bg-amber-100 text-amber-900 border-amber-200", icon: Pencil },
  admin:        { label: "Admin",        color: "bg-rose-100 text-rose-900 border-rose-200",    icon: ShieldAlert },
};

/* Origin tag — distinguishes Order-side ledger rows (payments, voids,
 * refunds, item edits) from Appointment-side audit rows (booked,
 * moved, specialist changed, cancelled) inside the same unified
 * timeline. The backend stamps `event.source` on every entry. */
const SOURCE_META = {
  order:       { label: "Order",       color: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  appointment: { label: "Appointment", color: "bg-teal-50 text-teal-800 border-teal-200" },
};

/* Actor origin — for booking_created rows we surface WHERE the click
 * came from so a name that appears in both the staff directory AND
 * the customer directory (a nurse whose customer profile inherits her
 * staff full_name, a guest and a staff member sharing "Ash Farrell",
 * etc.) is disambiguated at a glance. Populated from the audit row's
 * `by_kind` field (iter-375). Falls back to no badge for legacy rows
 * that predate the field. */
const ACTOR_KIND_META = {
  staff:    { label: "Staff",   color: "bg-slate-100 text-slate-700 border-slate-300" },
  customer: { label: "Guest",   color: "bg-amber-50 text-amber-800 border-amber-200" },
};

const KIND_ICON = {
  created: PlusCircle,
  closed: CheckCircle2,
  status: RefreshCcw,
  reopened: RefreshCcw,
  payment: CreditCard,
  payment_removed: X,
  item_added: PlusCircle,
  item_removed: Trash2,
  item_adjusted: Pencil,
  item_staff: UserIcon,
  item_quantity: Pencil,
  special_applied: Percent,
  special_removed: X,
  treatment_date: CalendarIcon,
  loyalty: Ticket,
  loyalty_restored: Ticket,
  loyalty_earned: Ticket,
  loyalty_earn_reversed: Ticket,
  loyalty_refund_credit: Ticket,
  merged: Users,
  merged_in: Users,
  unmerged: Users,
  appointment_scheduled: CalendarIcon,
  appointment_moved: CalendarIcon,
  specialist_changed: UserIcon,
  duration_updated: Clock,
  status_update: RefreshCcw,
  link_group_changed: Users,
  voided: Ban,
  refunded: RefreshCcw,
  deposit_conversion: RefreshCcw,
  deposit_refunded: RefreshCcw,
};

export function OrderHistoryPanel({ orderId, refreshKey, sectionRef }) {
  const [events, setEvents] = useState(null);   // null = loading
  const [filter, setFilter] = useState("all");  // all | booking | payment | modification | lifecycle | admin

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Cache-bust every fetch so a stale browser copy never masks a
        // freshly-written payment / void / refund event.
        const r = await staffApi.get(`/orders/${orderId}/events`, { params: { _ts: Date.now() } });
        if (!cancelled) setEvents(r.data || []);
      } catch {
        if (!cancelled) setEvents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId, refreshKey]);

  const filtered = useMemo(() => {
    if (!events) return [];
    const list = filter === "all" ? events : events.filter((e) => (e.category || "modification") === filter);
    // Newest-first for the audit trail (spec: most-recent event on top).
    // Sort by `at` (creation timestamp) descending, using the raw ISO
    // string — lexicographic order on ISO-8601 UTC matches chronological
    // order exactly. Non-mutating copy so we don't stomp the fetch cache.
    return [...list].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  }, [events, filter]);

  // Group by Bali date for the day-header timeline treatment. Because
  // `filtered` is now newest-first, iterating it in order yields
  // day-buckets in reverse-chronological order (today first, older
  // days below) — and each day's events are already newest-on-top
  // within the bucket, matching the section's overall sort direction.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of filtered) {
      const d = e.bali_date || (e.at ? e.at.slice(0, 10) : "unknown");
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(e);
    }
    return Array.from(map.entries()); // [[date, [events]], ...] in order
  }, [filtered]);

  const counts = useMemo(() => {
    const c = { all: (events || []).length };
    for (const e of (events || [])) {
      const k = e.category || "modification";
      c[k] = (c[k] || 0) + 1;
    }
    return c;
  }, [events]);

  return (
    <Card ref={sectionRef} data-testid="order-history-section" className="border-slate-200 scroll-mt-4">
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-slate-700" />
            <span className="uppercase tracking-wider text-xs font-semibold text-slate-800">History</span>
            <Badge variant="outline" className="rounded-full text-[10px]" data-testid="history-total-count">{counts.all || 0} events</Badge>
          </div>
          <div className="flex flex-wrap items-center gap-1">
            {["all","lifecycle","booking","payment","modification","admin"].map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`history-filter-${k}`}
                className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${filter === k ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-100"}`}
              >
                {k === "all" ? "All" : (CATEGORY_META[k]?.label || k)}
                <span className="ml-1 text-[10px] opacity-70">{counts[k] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="p-3">
          {events === null ? (
            <p className="text-sm text-muted-foreground" data-testid="history-loading">Loading timeline…</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="history-empty">No events match this filter yet.</p>
          ) : (
            <div className="space-y-2">
              {grouped.map(([day, dayEvents]) => (
                <div key={day} data-testid={`history-day-${day}`}>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 border-b border-slate-100 pb-0.5">
                    {day === "unknown" ? "Unknown day" : new Date(`${day}T00:00:00`).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Makassar" })}
                    <span className="text-slate-400 ml-2">· {dayEvents.length} event{dayEvents.length === 1 ? "" : "s"}</span>
                  </div>
                  <ul className="relative pl-3.5 border-l border-slate-200 space-y-1">
                    {dayEvents.map((e) => {
                      const cat = e.category || "modification";
                      const meta = CATEGORY_META[cat] || CATEGORY_META.modification;
                      const KIcon = KIND_ICON[e.kind] || meta.icon;
                      const timeStr = e.at ? new Date(e.at).toLocaleTimeString("en-GB", { timeZone: "Asia/Makassar", hour: "2-digit", minute: "2-digit" }) : "";
                      return (
                        <li key={e.id} className="relative leading-tight" data-testid={`history-event-${e.id}`}>
                          <span className={`absolute -left-[18px] top-[1px] flex items-center justify-center w-[13px] h-[13px] rounded-full border ${meta.color}`}>
                            <KIcon className="w-[7px] h-[7px]" />
                          </span>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0 text-[10px] text-muted-foreground leading-tight">
                              <span className="font-mono">{timeStr}</span>
                              {(() => {
                                const src = e.source === "appointment" ? SOURCE_META.appointment : SOURCE_META.order;
                                return (
                                  <span
                                    className={`px-1 py-0 rounded-full text-[9px] border font-medium ${src.color}`}
                                    data-testid={`history-event-${e.id}-source`}
                                  >
                                    {src.label}
                                  </span>
                                );
                              })()}
                              <span className={`px-1 py-0 rounded-full text-[9px] border ${meta.color}`} data-testid={`history-event-${e.id}-category`}>{meta.label}</span>
                              {(() => {
                                const ak = ACTOR_KIND_META[e.by_kind];
                                if (!ak) return null;
                                return (
                                  <span
                                    className={`px-1 py-0 rounded-full text-[9px] border ${ak.color}`}
                                    data-testid={`history-event-${e.id}-actor-kind`}
                                    title={e.by_kind === "customer" ? "Booked via guest self-service" : "Booked by staff"}
                                  >
                                    {ak.label}
                                  </span>
                                );
                              })()}
                              <span className="text-foreground font-medium truncate">{e.by_name || "system"}</span>
                            </div>
                            <div className="text-[11px] text-slate-800 leading-snug" data-testid={`history-event-${e.id}-desc`}>{e.description}</div>
                            {typeof e.amount === "number" && (
                              <div className="text-[10px] text-emerald-800 font-mono leading-tight" data-testid={`history-event-${e.id}-amount`}>{e.amount.toLocaleString("en-US")}</div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/* ---------------- History dialog (kept for the header icon shortcut) ---------------- */
export function HistoryDialog({ open, onOpenChange, order }) {
  // Fetch from the permanent `order_events` collection every time the
  // dialog opens so the audit trail is authoritative — the local
  // `order.events` blob can be stale (or partially loaded) after
  // background mutations, which used to look like "history vanished
  // after logout". Now the audit trail is always DB-sourced.
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open || !order?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await staffApi.get(`/orders/${order.id}/events`, { params: { _ts: Date.now() } });
        if (!cancelled) {
          const list = r.data || [];
          setEvents([...list].sort((a, b) => (b.at || "").localeCompare(a.at || "")));
        }
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, order?.id]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle>Order history</DialogTitle>
          <DialogDescription>Everything that happened on this order — creation, item changes, adjustments, payments.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[420px] overflow-auto space-y-2 pr-1">
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : events.length === 0 ? <p className="text-sm text-muted-foreground">No events yet.</p> : events.map((e) => {
            const src = e.source === "appointment" ? SOURCE_META.appointment : SOURCE_META.order;
            return (
              <div key={e.id} className="flex items-start gap-3 text-sm" data-testid={`history-event-${e.id}-dialog`}>
                <Clock className="w-3.5 h-3.5 mt-1 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground flex flex-wrap items-center gap-1.5">
                    <span>{fmtDateTime(e.at)}</span>
                    <span
                      className={`px-1 py-0 rounded-full text-[9px] border font-medium ${src.color}`}
                      data-testid={`history-event-${e.id}-dialog-source`}
                    >
                      {src.label}
                    </span>
                    <span className="font-medium text-foreground">{e.by_name || "system"}</span>
                  </div>
                  <div className="text-sm">{e.description}</div>
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
